import {
  EventDoc,
  GroupDoc,
  GroupStatus,
  UserSettingsDoc,
  UserSettingsGroupSettings
} from 'types/api/changes'

import {
  outlookPrefix,
  icloudPrefix,
  prefixGoogleEventsGroup
} from './constants'
import { APP_COLOR } from './colors'

export enum PersonalGroupSyncStatus {
  PENDING = '1',
  SYNCED = '2',
  PRIVATE = '3'
}
import { isTrulyEmpty } from './object'
import { getCachedConvertedCalendarsFromLocalStorage } from './third_party_calendars'
import { isGroupArchived, isGroupHidden } from './api/userSettings'
import { Model } from 'types/model'
import { UserSettings } from 'services/db/Queries/UserSettingsQueries'
import { performUpdateGroupSettings } from 'components/popups/groupInfo/SettingsForCalendarSection'
import Logger from 'services/Logger'
import { isOwnerForGroup } from 'services/db/utils/GroupUtils'
import {
  buildRemoveParticipantData,
  transferOwnershipOfAGroup as transferOwnershipOfAGroup,
  updateGroupOnServer
} from 'services/api/group'

import { LocalStorageKey } from 'types/services/localStorage'
import LocalStorage from 'services/LocalStorage'
import { User } from '@sentry/browser'
import { eventCalendarIds } from './calendarInheritance'

// Define the types of third-party providers you support
export enum ProviderType {
  GOOGLE = 'Google',
  OUTLOOK = 'Outlook',
  APPLE = 'iCloud'
  // Add more providers here
}

export function isEventHiddenInAllGroups(
  group: GroupDoc | undefined,
  event: EventDoc | undefined,
  userSettings: UserSettingsDoc | undefined
): boolean {
  if (!group || !event || !userSettings) return false

  const relatedGroupStates = []

  for (const groupId of eventCalendarIds(event)) {
    if (groupId === event.GroupID) continue
    const groupState = isGroupHidden(userSettings, groupId)
    relatedGroupStates.push(
      groupState || isGroupArchived(userSettings, groupId)
    )
  }

  const isAllHiddenOnAllCalendars =
    relatedGroupStates.length === 0 ||
    relatedGroupStates.every((state) => state === true)

  if (
    isGroupHidden(userSettings, event.GroupID ?? '') &&
    isAllHiddenOnAllCalendars
  ) {
    return true
  }

  return false
}

export function getGroupColor(
  groupDoc: GroupDoc | undefined | null,
  groupsSettings: Record<string, UserSettingsGroupSettings> | undefined
) {
  if (!groupDoc) return APP_COLOR

  const groupItselfColor = groupDoc.GroupColor

  return groupsSettings?.[groupDoc._id ?? '']?.GroupColor ?? groupItselfColor
}

export function defaultGroupId(userSettings?: UserSettingsDoc) {
  if (!userSettings) return undefined

  return Object.entries(userSettings?.GroupsSettings ?? {}).find(
    ([key, value]) => value.Default === '1'
  )?.[0]
}

export function isUsersCanAddEvents(group: GroupDoc | undefined) {
  if (!group) return false
  return group.GroupSettings?.IsAllParticipantsCanAddItems === '1'
}

/**
 *
 * @param group to check if it is a converted group
 * @returns true if id contains prefix of a converted group
 */
export function isConvertedGroup(group: GroupDoc | undefined) {
  if (!group) return false
  return (
    isConvertedGroupId(group._id?.toLowerCase() ?? '') ||
    isConvertedGroupId(group.CalendarEmail?.toLocaleLowerCase() ?? '')
  )
}

export function isConvertedGroupId(groupId?: string) {
  if (isTrulyEmpty(groupId)) return false

  const calendar = getCachedConvertedCalendarsFromLocalStorage().find(
    (cal) => cal.localId === groupId
  )

  if (calendar) return true
  return [
    prefixGoogleEventsGroup.toLowerCase(),
    outlookPrefix.toLowerCase(),
    icloudPrefix.toLowerCase()
  ].some((prefix) => groupId?.toLowerCase().includes(prefix))
}

export function groupIdToProviderTypeName(groupId?: string) {
  const calendar = getCachedConvertedCalendarsFromLocalStorage().find(
    (cal) => cal.localId === groupId
  )

  if (calendar) {
    if (calendar.prefix === prefixGoogleEventsGroup) return 'Google'
    if (calendar.prefix === outlookPrefix) return 'Outlook'
    if (calendar.prefix === icloudPrefix) return 'iCloud'
    return undefined
  }

  if (!groupId) return undefined
  if (groupId.includes(outlookPrefix)) return 'Outlook'
  if (groupId.includes(prefixGoogleEventsGroup)) return 'Google'
  if (groupId.includes(icloudPrefix)) return 'iCloud'

  return undefined
}

export function groupIdToProvider(groupId: string): ProviderType | undefined {
  const calendar = getCachedConvertedCalendarsFromLocalStorage().find(
    (cal) => cal.localId === groupId
  )

  if (calendar) {
    if (calendar.prefix === prefixGoogleEventsGroup) return ProviderType.GOOGLE
    if (calendar.prefix === outlookPrefix) return ProviderType.OUTLOOK
    if (calendar.prefix === icloudPrefix) return ProviderType.APPLE
    return undefined
  }

  if (!groupId) return undefined
  if (groupId.includes(prefixGoogleEventsGroup)) return ProviderType.GOOGLE
  if (groupId.includes(outlookPrefix)) return ProviderType.OUTLOOK
  if (groupId.includes(icloudPrefix)) return ProviderType.APPLE
  return undefined
}

export function groupIdToPrefix(groupId: string): string | undefined {
  if (!groupId) return undefined
  if (groupId.includes(prefixGoogleEventsGroup)) return prefixGoogleEventsGroup
  if (groupId.includes(outlookPrefix)) return outlookPrefix
  if (groupId.includes(icloudPrefix)) return icloudPrefix
  return undefined
}

export function groupToProvider(
  group: GroupDoc | undefined
): ProviderType | undefined {
  if (!group) return undefined

  if (
    group.CalendarEmail?.includes(prefixGoogleEventsGroup) ||
    group._id?.includes(prefixGoogleEventsGroup)
  )
    return ProviderType.GOOGLE
  if (
    group.CalendarEmail?.includes(outlookPrefix) ||
    group._id?.includes(outlookPrefix)
  )
    return ProviderType.OUTLOOK
  if (
    group.CalendarEmail?.includes(icloudPrefix) ||
    group._id?.includes(icloudPrefix)
  )
    return ProviderType.APPLE
  return undefined
}

export function changeGroupStatus(
  group: Model<GroupDoc>,
  status: GroupStatus = GroupStatus.ARCHIVED
) {
  if (!isTrulyEmpty(group._rev)) {
    // if group removed successfully, we need to archive it on server as well
    return UserSettings().then((userSettings) => {
      Logger.log('Deleting group with id ', group._id, ' and status ', status)
      return performUpdateGroupSettings(userSettings, group._id, {
        GroupStatus: status
      }).catch((error) => {
        Logger.red('Error during change group status ', error)
      })
    })
  }

  return Promise.resolve()
}

export function toggleArchiveGroup(
  group: Model<GroupDoc> | GroupDoc,
  userSettings: Model<UserSettingsDoc> | UserSettingsDoc | undefined
) {
  const newValue = isGroupArchived(userSettings, group._id ?? '')
    ? GroupStatus.ACTIVE
    : GroupStatus.ARCHIVED
  return performUpdateGroupSettings(userSettings, group._id ?? '', {
    GroupStatus: newValue
  })
}

export async function exitFromGroup(group: Model<GroupDoc>) {
  const userId = LocalStorage.get(LocalStorageKey.USER_ID) ?? ''

  const isOwner = isOwnerForGroup(group)

  if (isOwner) {
    // if we are owner, check is there other active member to transfer ownership to
    const firstActiveMember = Object.entries(group.Participants).find(
      ([key, participant]) =>
        participant.Status === '1' && key !== group.OwnerID
    )

    if (firstActiveMember) {
      // if there is another active member, we transfer ownership to him and then exit from group
      try {
        await transferOwnershipOfAGroup(
          group._id ?? '',
          firstActiveMember[0]
        ).then(() => {
          return updateGroupOnServer(
            buildRemoveParticipantData(
              group.Participants[userId],
              userId,
              group._id ?? ''
            ),
            group
          )
        })
      } catch (e) {
        Logger.red(
          'Error during transferring ownership and exiting from group ',
          e
        )
      }
      return
    }
  }

  return updateGroupOnServer(
    buildRemoveParticipantData(group.Participants[userId], userId, group._id),
    group
  ).then(() => {
    LocalStorage.remove(LocalStorageKey.SHOWING_GROUP_INFO)
  })
}

export async function deleteGroupForUser(group: Model<GroupDoc>) {
  const userId = LocalStorage.get(LocalStorageKey.USER_ID) ?? ''

  async function softDeleteGroup() {
    // first we exit from a group
    await updateGroupOnServer(
      buildRemoveParticipantData(group.Participants[userId], userId, group._id),
      group
    )
      .then(() => {
        // we mark group as status 3
        return changeGroupStatus(group, GroupStatus.DELETED)
      })
      .then(() => {
        Logger.red(
          'Group with id ',
          group._id,
          ' was marked as deleted on server for non owner'
        )
      })
  }

  if (!isOwnerForGroup(group)) {
    await softDeleteGroup()
  } else {
    const firstActiveMember = Object.entries(group.Participants).find(
      ([key, participant]) =>
        participant.Status === '1' && key !== group.OwnerID
    )

    if (firstActiveMember) {
      // if there is another active member, we transfer ownership to him and then exit from group
      try {
        await transferOwnershipOfAGroup(
          group._id ?? '',
          firstActiveMember[0]
        ).then(() => {
          return softDeleteGroup()
        })
      } catch (e) {
        Logger.red(
          'Error during transferring ownership and exiting from group ',
          e
        )
      }
    } else {
      // if there is no active members, we can delete group from server directly
      await softDeleteGroup()
    }
  }
}
